import { useI18n } from "@core/i18n/i18n";
import { getLogger } from "@core/logging/logger";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { useContext, useRef, useState } from "react";
import { useNavigate as useReactRouterNavigate } from "react-router";
import { useBeforeUnload, useBlocker } from "react-router-dom";
import type { Location } from "react-router-dom";

export function useQuitBlocking(pendingChanges: boolean): void {
	const logger = getLogger("🦺 [useQuitBlocking]");
	const { format } = useI18n();
	const message = format("global.nativeBlockModal");
	const dialogContext = useContext(DialogContext);
	const navigate = useReactRouterNavigate();

	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const pendingNavigationRef = useRef<Location | null>(null);

	useBeforeUnload(event => {
		if (pendingChanges) {
			logger.debug("Navigation blocked, changes have been detected while reloading the page");
			event.preventDefault();
		}
	});

	useBlocker(({ currentLocation, nextLocation }) => {
		if (dialogOpen || !pendingChanges) {
			logger.debug("Navigation unblocked: dialogOpen = ", dialogOpen, " pendingChanges", pendingChanges);
			return false;
		}
		logger.debug("Open dialog");
		pendingNavigationRef.current = nextLocation;
		setDialogOpen(true);
		dialogContext?.openModal(ModalId.SaveChanges, {
			onConfirm: () => {
				const target = pendingNavigationRef.current;
				pendingNavigationRef.current = null;
				logger.debug(
					"Confirm pressed, navigating to next location : ",
					target,
					" from current location : ",
					currentLocation,
				);
				if (target) {
					navigate(target.pathname + target.search + target.hash);
				}
			},
			onCancel: () => {
				pendingNavigationRef.current = null;
				setDialogOpen(false);
			},
			message: message,
		});
		return pendingChanges;
	});
}
